/** ERP module URL prefixes. Pages live under the matching first segment. */
export const ERP_HOSPITALITY_SLUG = 'hospitality';
export const ERP_SALES_SLUG = 'sales-distribution';
export const ERP_REPORTING_SLUG = 'reporting-analytics';
export const ERP_CRM_SLUG = 'crm';

export const ERP_HOSPITALITY_BASE = `/${ERP_HOSPITALITY_SLUG}`;
export const ERP_SALES_BASE = `/${ERP_SALES_SLUG}`;
export const ERP_REPORTING_BASE = `/${ERP_REPORTING_SLUG}`;
export const ERP_CRM_BASE = `/${ERP_CRM_SLUG}`;

export type ErpModuleId =
  | 'hospitality'
  | 'sales_distribution'
  | 'reporting_analytics'
  | 'crm';

/** Former Hospitality sidebar page (rooms). */
export const OPERATIONS_SLUG = 'operations';

const joinModulePath = (
  base: string,
  ...segments: Array<string | number | null | undefined>
) => {
  const rest = segments
    .filter((s) => s !== null && s !== undefined && String(s).length > 0)
    .map((s) => String(s).replace(/^\/+|\/+$/g, ''))
    .filter(Boolean)
    .join('/');
  return rest ? `${base}/${rest}` : base;
};

export const hospitalityModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_HOSPITALITY_BASE, ...segments);

export const salesModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_SALES_BASE, ...segments);

export const reportingModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_REPORTING_BASE, ...segments);

export const crmModulePath = (
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_CRM_BASE, ...segments);

export const ERP_MODULE_BASE: Record<ErpModuleId, string> = {
  hospitality: ERP_HOSPITALITY_BASE,
  sales_distribution: ERP_SALES_BASE,
  reporting_analytics: ERP_REPORTING_BASE,
  crm: ERP_CRM_BASE,
};

export const ERP_MODULE_IDS: ErpModuleId[] = [
  'hospitality',
  'sales_distribution',
  'reporting_analytics',
  'crm',
];

export const erpModulePath = (
  moduleId: ErpModuleId,
  ...segments: Array<string | number | null | undefined>
) => joinModulePath(ERP_MODULE_BASE[moduleId], ...segments);

/** Settings URL under a given ERP module (`/reporting-analytics/settings/hotel`). */
export const erpSettingsPath = (
  moduleId: ErpModuleId,
  ...segments: Array<string | number | null | undefined>
) => erpModulePath(moduleId, 'settings', ...segments);

/** All `/<module>/settings` route patterns for Routing. */
export const erpSettingsRoutePaths = (): string[] => (
  ERP_MODULE_IDS.flatMap((id) => [
    `${erpSettingsPath(id)}/:subtab/:plan`,
    `${erpSettingsPath(id)}/:subtab`,
    erpSettingsPath(id),
  ])
);

export const HOSPITALITY_PATHS = {
  operations: hospitalityModulePath(OPERATIONS_SLUG),
  operationsActions: (id: string | number) => (
    hospitalityModulePath(OPERATIONS_SLUG, 'actions', id)
  ),
  dashboard: hospitalityModulePath('dashboard'),
  sales: salesModulePath('sales'),
  refunds: salesModulePath('refunds'),
  reservations: hospitalityModulePath('reservations'),
  walkIns: hospitalityModulePath('walk-ins'),
  rooms: hospitalityModulePath('rooms'),
  roomsTab: (subtab: string) => hospitalityModulePath('rooms', subtab),
  roomsEditRoom: (id: string | number) => (
    hospitalityModulePath('rooms', 'editroom', id)
  ),
  departments: salesModulePath('departments'),
  departmentsSub: (subtab: string) => salesModulePath('departments', subtab),
  inventory: salesModulePath('inventory'),
  audits: reportingModulePath('audits'),
  auditsAction: (action: string | number) => reportingModulePath('audits', action),
  metrics: reportingModulePath('metrics'),
  reporting: reportingModulePath('reporting'),
  reportingTab: (subtab: string) => reportingModulePath('reporting', subtab),
  crm: ERP_CRM_BASE,
  crmTab: (tab: string) => crmModulePath(tab),
  crmMessage: (tab: string, messageId: string) => (
    crmModulePath(tab, messageId)
  ),
  settings: hospitalityModulePath('settings'),
  settingsSub: (subtab: string) => hospitalityModulePath('settings', subtab),
  settingsPlan: (subtab: string, plan: string) => (
    hospitalityModulePath('settings', subtab, plan)
  ),
  /** Edit-room lives under Room setup, not Settings. */
  settingsEditRoom: (id: string | number) => (
    hospitalityModulePath('rooms', 'editroom', id)
  ),
  settingsAccount: hospitalityModulePath('settings', 'account'),
  reserve: hospitalityModulePath('reserve'),
};

export const hospitalityPageSlug = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== ERP_HOSPITALITY_SLUG) return '';
  return parts[1] || '';
};

/** Second URL segment for any ERP module (`/crm/feedback` → `feedback`). */
export const erpPageSlug = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[1] || '';
};

export const getErpModuleFromPath = (pathname: string): ErpModuleId | null => {
  const root = pathname.split('/').filter(Boolean)[0];
  if (root === ERP_HOSPITALITY_SLUG) return 'hospitality';
  if (root === ERP_SALES_SLUG) return 'sales_distribution';
  if (root === ERP_REPORTING_SLUG) return 'reporting_analytics';
  if (root === ERP_CRM_SLUG) return 'crm';
  return null;
};

/** Settings URL that keeps the module of the current pathname. */
export const erpSettingsPathFromPathname = (
  pathname: string,
  ...segments: Array<string | number | null | undefined>
) => erpSettingsPath(getErpModuleFromPath(pathname) || 'hospitality', ...segments);

export const isHospitalityModulePath = (pathname: string): boolean => (
  hospitalityPageSlug(pathname) !== '' || pathname === ERP_HOSPITALITY_BASE
    || pathname.startsWith(`${ERP_HOSPITALITY_BASE}/`)
);

export const isErpAppPath = (pathname: string): boolean => (
  getErpModuleFromPath(pathname) !== null
);

/** True for `/hospitality/operations` and nested segments. */
export const isHospitalityPagePath = (pathname: string, page: string): boolean => (
  hospitalityPageSlug(pathname) === page
);

/** True for `/sales-distribution/departments` and nested segments. */
export const isErpPagePath = (pathname: string, page: string): boolean => (
  erpPageSlug(pathname) === page
);

/** Exact hospitality page with no extra segments. */
export const isExactHospitalityPage = (pathname: string, page: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[0] === ERP_HOSPITALITY_SLUG && parts[1] === page && parts.length === 2;
};

/** Exact ERP page regardless of module prefix (`/reporting-analytics/audits`). */
export const isExactErpPage = (pathname: string, page: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return parts[1] === page && parts.length === 2;
};

export const isErpSettingsAccount = (pathname: string): boolean => {
  const parts = pathname.split('/').filter(Boolean);
  return getErpModuleFromPath(pathname) !== null
    && parts[1] === 'settings'
    && parts[2] === 'account';
};
